import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import ReplacementAPI from 'api/faber/ReplacementAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import OrbitSession from 'hooks/useCookies'
import { EsimInfo, EsimsInfoPaginated } from 'types/api/faber/dataUsage.type'

import EsimRequestV3Dialog from './EsimRequestV3Dialog'

interface EsimsInfoProps {
  userId?: string
  // faberToken: string
  // updateUserDetails: () => void
  open: boolean
  onClose: () => void
  esimsInfo?: EsimsInfoPaginated
  pageNumber: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  pageCount: number
  onAddUserPlan?: (iccid: string) => void 
  handleReprovisionClick?: (iccid: string) => void
  handleReplaceClick?: (iccid: string) => void
}

const columns = (role: string, onAddUserPlan?: (iccid: string) => void, handleReprovisionClick?: (iccid: string) => void, handleReplaceClick?: (iccid: string) => void ) => [
  {
    field: 'iccid',
    headerName: 'ICCID',
    sortable: false,
    width: 400
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    width: 80
  },
  {
    field: 'imsiType',
    headerName: 'IMSI Type',
    sortable: false,
    width: 80
  },
  {
    field: 'esimDefaultWhitelist',
    headerName: 'Default Whitelist',
    sortable: false,
    width: 80
  },
  {
    field: 'esimCurrentWhitelist',
    headerName: 'Current Whitelist',
    sortable: false,
    width: 80
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    minWidth: 60,
    renderCell: (params: { row: EsimInfo }) => {
      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

      const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
      }
      const handleClose = () => {
        setAnchorEl(null)
      }

      return (
        <>
          <IconButton onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: { minWidth: 150 }
            }}
          >
            {role === 'opsowner' && (
              <MenuItem onClick={() => onAddUserPlan?.(params.row.iccid)}>
                Add Plan
              </MenuItem>
            )}

            <MenuItem
              onClick={() => {
                handleReprovisionClick?.(params.row.iccid)
                handleClose()
              }}
            >
              Reprovision
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleReplaceClick?.(params.row.iccid)
                handleClose()
              }}
            >
              Replace
            </MenuItem>
          </Menu>
        </>
      )
    }
  }
]

const UserEsimsStatusLogsDialog: React.FC<EsimsInfoProps> = ({
  userId,
  open,
  onClose,
  esimsInfo,
  pageNumber,
  setPage,
  pageCount,
  onAddUserPlan
}) => {
  const theme = useTheme()
  const faberToken = OrbitSession.get('faberToken')
  const role = OrbitSession.get('role')

  const [confirmDialogOpenReprovision, setConfirmDialogOpenReprovision] =
    useState(false)
  const [confirmDialogOpenReplace, setConfirmDialogOpenReplace] = useState(false)

  const [esimICCID, setEsimICCID] = useState( "" ) 

  /*TO POST ESIM REPLACMENT*/
  const { mutate: mutationReprovisionUpdateEsim, isLoading: isReprovisionLoading } =
    useMutation(ReplacementAPI.esimReplacementUpdate, {
      onSuccess: () => {
        toast.success('eSim has been updated for selected customer')
        setConfirmDialogOpenReprovision(false)
      },
      onError: () => {
        toast.error('Error requesting for new esim')
      }
    })

  const handleReprovisionClick = (iccid: string) => {
    setEsimICCID(iccid)
    setConfirmDialogOpenReprovision(true)
  }

  const handleConfirmReprovision = async () => {
    await mutationReprovisionUpdateEsim({
      userId: userId ?? '',
      newICCID: esimICCID ?? '',
      faberToken: faberToken ?? ''
    })
  }

  const handleCancelReprovision = () => {
    setConfirmDialogOpenReprovision(false)
  }

  const handleReplaceClick = (iccid: string) => {
    setEsimICCID(iccid)
    setConfirmDialogOpenReplace(true)
  }

  const handleCancelReplace = () => {
    setConfirmDialogOpenReplace(false)
  }

  return (
    <>
      {/* Main eSIMs Dialog */}
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        PaperProps={{
          style: {
            width: '70%',
            maxWidth: 'none',
            padding: '10px'
          }
        }}
      >
        <DialogTitle>User eSims</DialogTitle>
        <Divider />
        <DialogContent>
          <MUIDataGrid
            getRowId={(row) => `${row.iccid}-${row.iccid}`}
            columns={columns(
              role ?? "",
              onAddUserPlan,
              handleReprovisionClick,
              handleReplaceClick
            )}
            rows={esimsInfo?.body?.data ?? []}
            pageCount={pageCount}
            pageNumber={pageNumber}
            onPageChange={(newPageNumber) => setPage(newPageNumber - 1)}
            disableColumnFilter
            disableColumnMenu
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none'
              },
              '& .MuiDataGrid-cell': {
                py: 1
              },
              [theme.breakpoints.down('sm')]: {
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontSize: '0.85rem'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '0.8rem'
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions style={{ margin: '-10px 12px 10px 0' }}>
          <Button onClick={onClose} color='primary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Reprovision eSIM Dialog */}
      <Dialog
        open={confirmDialogOpenReprovision}
        onClose={handleCancelReprovision}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Confirm eSIM Reprovision</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reprovision the eSIM with ICCID:{' '}
            <strong>{esimICCID}</strong>?
          </DialogContentText>
          <DialogContentText
            sx={{ mt: 2, fontSize: '0.9rem', color: 'text.secondary' }}
          >
            The current eSIM will be deactivated and reprovision with a new one.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isReprovisionLoading}
            onClick={handleCancelReprovision}
            color='inherit'
          >
            Cancel
          </Button>
          <Button
            disabled={isReprovisionLoading}
            onClick={handleConfirmReprovision}
            variant='contained'
            autoFocus
          >
            Reprovision eSIM
          </Button>
        </DialogActions>
      </Dialog>

      <EsimRequestV3Dialog
        handleRequestEsimDialogClose={() => handleCancelReplace()}
        requestEsimDialog={confirmDialogOpenReplace}
        userId={userId ?? ''}
        esimICCID={esimICCID}
        faberToken={faberToken}
        isExternal={false}
      />
    </>
  )
}

export default UserEsimsStatusLogsDialog
